import { baseApi } from './base';
import type {
  Bill,
  CreateBillRequest,
  UpdateBillRequest,
  BillTotals,
} from '@/types/financial';
import type { UpdateForwardPayload } from '@/types/recurrence';
import { mapBill } from '@/mappers/financial-mapper';

export const financialService = {
  async getBills(params: { limit: number; offset: number; ordering: string; search?: string; type?: string; date?: string; }): Promise<{ results: Bill[], count: number }> {
    const queryParams: Record<string, string> = {
      limit: params.limit.toString(),
      offset: params.offset.toString(),
      ordering: params.ordering,
    };

    if (params.search) {
      queryParams.search = params.search;
    }

    if (params.type && params.type !== 'all') {
      queryParams.type = params.type;
    }

    if (params.date) {
      queryParams.date = params.date;
    }

    const query = new URLSearchParams(queryParams).toString();
    return baseApi.request<{ results: Bill[], count: number }>(`/payments/?${query}`)
      .then(res => ({
        ...res,
        results: res.results.map(mapBill)
      }));
  },
  
  async getBillsTotals(params: { search?: string; type?: string; date?: string; tags?: string; lawsuit?: string }): Promise<BillTotals> {
    const queryParams: Record<string, string> = {};
    if (params.search) queryParams.search = params.search;
    if (params.type && params.type !== 'all') queryParams.type = params.type;
    if (params.date) queryParams.date = params.date;
    if (params.tags) queryParams.tags = params.tags;
    if (params.lawsuit) queryParams.lawsuit = params.lawsuit;

    const query = new URLSearchParams(queryParams).toString();
    return baseApi.request<BillTotals>(`/payments/totals/?${query}`);
  },

  async createBill(data: CreateBillRequest): Promise<Bill> {
    return baseApi.request<Bill>('/payments/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateBill(id: string, data: UpdateBillRequest): Promise<Bill> {
    return baseApi.request<Bill>(`/payments/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteBill(id: string): Promise<void> {
    return baseApi.request<void>(`/payments/${id}/`, {
      method: 'DELETE',
    });
  },

  async settleBill(id: string, data: { date: string; paid_at: string; paid: boolean; total: number }): Promise<Bill> {
    return baseApi.request<Bill>(`/payments/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async cancelForwardBill(id: string): Promise<void> {
    return baseApi.request<void>(`/payments/${id}/cancel_forward/`, {
      method: 'POST',
    });
  },

  async updateForwardBill(id: string, data: UpdateForwardPayload): Promise<{ updated: number }> {
    return baseApi.request<{ updated: number }>(`/payments/${id}/update_forward/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};
